// User Roles Enum - Maps to backend Role enum
//
// Los valores son los ORDINALES del enum `Role` del backend
// (DomainService/.../entities/enums/Role.java). El backend manda los roles del usuario como
// STRINGS en el login (`user.roles`), asi que estos numeros se usan para numerarlos aca.
// Si el backend agrega un rol, agregarlo SIEMPRE al final, igual que alla.
export const USER_ROLES = Object.freeze({
  PASSENGER: 0,
  DRIVER: 1,
  DRIVER_ADMIN: 2,
  EMPLOYEE: 3,
  EMPLOYEE_ADMIN: 4,
  BUSOWNER: 5,
  COMPANY: 6,
  COMPANY_ADMIN: 7,
  GOVERNMENT: 8,
  GOVERNMENT_ADMIN: 9,
  WHATSAPP: 10,
  INVESTOR: 11,
  INVESTOR_ADMIN: 12,
  TERMINAL_MANAGER: 13,
  STOP_COORDINATOR: 14, // Fiscal
  INTERNAL_SERVICE: 15,
  BILLER: 16,
  ACCOUNTANT: 17,
  SYNDICALIST: 18,
  TRANSIT_OPERATOR: 19,
  TRANSIT_VALIDATOR: 20, // El torniquete como usuario: dispositivo, no persona
});

// Reverse mapping: number -> string key
export const ROLE_KEYS = Object.freeze({
  0: "PASSENGER",
  1: "DRIVER",
  2: "DRIVER_ADMIN",
  3: "EMPLOYEE",
  4: "EMPLOYEE_ADMIN",
  5: "BUSOWNER",
  6: "COMPANY",
  7: "COMPANY_ADMIN",
  8: "GOVERNMENT",
  9: "GOVERNMENT_ADMIN",
  10: "WHATSAPP",
  11: "INVESTOR",
  12: "INVESTOR_ADMIN",
  13: "TERMINAL_MANAGER",
  14: "STOP_COORDINATOR",
  15: "INTERNAL_SERVICE",
  16: "BILLER",
  17: "ACCOUNTANT",
  18: "SYNDICALIST",
  19: "TRANSIT_OPERATOR",
  20: "TRANSIT_VALIDATOR",
});

// Display names for UI
export const ROLE_NAMES = Object.freeze({
  [USER_ROLES.PASSENGER]: "Pasajero",
  [USER_ROLES.DRIVER]: "Conductor",
  [USER_ROLES.DRIVER_ADMIN]: "Admin",
  [USER_ROLES.EMPLOYEE]: "Personal",
  [USER_ROLES.EMPLOYEE_ADMIN]: "Gerente",
  [USER_ROLES.BUSOWNER]: "Dueño Unidad",
  [USER_ROLES.COMPANY]: "Compañía",
  [USER_ROLES.COMPANY_ADMIN]: "Compañía Admin",
  [USER_ROLES.GOVERNMENT]: "Gobierno",
  [USER_ROLES.GOVERNMENT_ADMIN]: "Gobierno Gerente",
  [USER_ROLES.WHATSAPP]: "Whatsapp",
  [USER_ROLES.INVESTOR]: "Inversor",
  [USER_ROLES.INVESTOR_ADMIN]: "Inversor Admin",
  [USER_ROLES.TERMINAL_MANAGER]: "Gerente Terminal",
  [USER_ROLES.STOP_COORDINATOR]: "Fiscal",
  [USER_ROLES.INTERNAL_SERVICE]: "Servicio Interno",
  [USER_ROLES.BILLER]: "Facturador",
  [USER_ROLES.ACCOUNTANT]: "Contador",
  [USER_ROLES.SYNDICALIST]: "Sindicalista",
  [USER_ROLES.TRANSIT_OPERATOR]: "Operador",
  [USER_ROLES.TRANSIT_VALIDATOR]: "Validador",
});

// =============================================================================
//  QUIEN PUEDE ENTRAR A MAPS
// =============================================================================
// Lista blanca: cualquier rol que NO este aca no entra, ni siquiera a la pantalla de seleccion.
// PASSENGER queda afuera a proposito — maps es herramienta de operacion, no de pasajero.
//
// Para habilitar otro rol basta agregarlo aca: el login, el selector de rol y el guard de las
// rutas leen todos de esta misma lista.
export const MAPS_ALLOWED_ROLES = Object.freeze([
  "EMPLOYEE",
  "GOVERNMENT",
  "GOVERNMENT_ADMIN",
]);

// Roles de alcaldia: solo ven las lineas de SU alcaldia (SuperRoute.surGovId). El resto de los
// roles permitidos ve todo.
export const GOVERNMENT_ROLE_KEYS = Object.freeze(["GOVERNMENT", "GOVERNMENT_ADMIN"]);

/** Descripcion de cada rol para las tarjetas del selector. */
export const MAPS_ROLE_DESCRIPTIONS = Object.freeze({
  EMPLOYEE: "Acceso operativo completo: todas las líneas y rutas.",
  GOVERNMENT: "Solo las líneas de su alcaldía.",
  GOVERNMENT_ADMIN: "Solo las líneas de su alcaldía.",
});

/** `true` si el rol (key string) puede entrar a maps. */
export const isRoleAllowedInMaps = (roleKey) => MAPS_ALLOWED_ROLES.includes(roleKey);

/** `true` si el rol (key string) es de alcaldia y por tanto ve solo sus lineas. */
export const isGovernmentRole = (roleKey) => GOVERNMENT_ROLE_KEYS.includes(roleKey);

/**
 * De la lista de roles que manda el login (strings), deja solo los que entran a maps y sin
 * repetidos. Tolera que `roles` venga null/undefined o con numeros en vez de strings.
 */
export const filterRolesForMaps = (roles) => {
  if (!Array.isArray(roles)) return [];
  const normalized = roles
    .map((role) => (typeof role === "number" ? ROLE_KEYS[role] : role))
    .filter(Boolean);
  return [...new Set(normalized)].filter(isRoleAllowedInMaps);
};

/*
Usage examples:
- USER_ROLES.PASSENGER === 0
- ROLE_KEYS[9] === "GOVERNMENT_ADMIN"
- ROLE_NAMES[USER_ROLES.STOP_COORDINATOR] === "Fiscal"
- filterRolesForMaps(["PASSENGER","EMPLOYEE","GOVERNMENT_ADMIN"]) === ["EMPLOYEE","GOVERNMENT_ADMIN"]
*/
